import { IIntervalService, IRootScopeService, ITimeoutService, resource } from 'angular';
import { ILocalStorageService } from 'angular-local-storage';
import { ConfigService } from 'app/config/config.service';
import { AbstractFetcher } from 'app/shared/collections/time/fetcher/fetcher.factory';
import {
    AdjustableClosestPastSampleInterpolator,
    getDateWithinTimeRangeInterpolator,
} from 'app/shared/collections/time/interpolator/adjustable_closet_past_sample_interpolator';
import { AdjustableProcessor } from 'app/shared/collections/time/processor/AdjustableProcessor';
import { GroupedSampleProcessor } from 'app/shared/collections/time/processor/GroupedSampleProcessor';
import { SampleProcessor } from 'app/shared/collections/time/processor/SampleProcessor';
import { AbstractQueue } from 'app/shared/collections/time/queue/queue.factory';
import { getStatHoldSeconds } from 'app/shared/data_frequency_util';
import {
    createWithLocationEndpoint,
    DISTINCT_SUBGROUP_VALUES_KEY,
    WITH_LOCATION_ENDPOINT_FUNC,
} from 'app/shared/resources';
import { StartupRegistry } from 'app/startup/registry/registry';
import { DynamicStatSchema } from './collections/dynamic_stat_schema';
import { DynamicStatSchemaCollection } from './collections/dynamic_stat_schema_collection.service';
import { ImsObject, ImsObjectResource } from './collections/ims_object';
import { SimpleFetcher } from './collections/time/fetcher/simple_fetcher.factory';
import {
    ImsTimeCollection,
    ImsTimeCollectionOptions,
} from './collections/time/ims_time_collection.factory';
import { TimeIntervalChunker } from './collections/time/queue/time_interval_chunker.factory';
import { getParentFieldKeyForParentType } from './dynamic_stat_utils';
import { ImsItemTypeDetails, ImsItemTypeRegistry } from './ims_items/ImsItemTypeRegistry';
import { SiteStateService } from './site_state.service';
import { ClockHandler } from './time/clock_handler.service';
import { ViewStateService } from './view_state.service';

export interface DynamicStatObjectClass extends ImsObject {
    new (resource: ImsObjectResource);

    timestamp: string;
    timestampAsDate: Date;
}

interface DynamicStatImsObjectCollection extends ImsTimeCollection {
    new (
        $rootScope: IRootScopeService,
        $timeout: ITimeoutService,
        $interval: IIntervalService,
        clockHandler: ClockHandler,
        viewStateService: ViewStateService,
        siteStateService: SiteStateService,
        queue: AbstractQueue,
        fetcher: AbstractFetcher,
        processor: AdjustableProcessor<unknown>,
        options: ImsTimeCollectionOptions,
    );
}

type ConstructableClass = new (resource: ImsObjectResource) => InstanceType<DynamicStatObjectClass>;

type ConstructableDynamicStatImsObjectCollection = new (
    startUpRegistry: StartupRegistry,
    localStorageService: ILocalStorageService,
    $rootScope: IRootScopeService,
    $timeout: ITimeoutService,
    $interval: IIntervalService,
    clockHandler: ClockHandler,
    viewStateService: ViewStateService,
    siteStateService: SiteStateService,
    queue: AbstractQueue,
    fetcher: AbstractFetcher,
    processor: AdjustableProcessor<unknown>,
    options: ImsTimeCollectionOptions,
) => InstanceType<DynamicStatImsObjectCollection>;

function createImsObjectClass(
    dynamicStatSchema: DynamicStatSchema,
    dynamicStatResource: resource.IResourceClass<unknown>,
): ConstructableClass {
    const statTypeName = dynamicStatSchema.name;

    // Create IMSObject subclass for this dynamic stat
    const DynamicStatImsObjectClass = class extends ImsObject {
        constructor(resource = new dynamicStatResource()) {
            super(statTypeName, resource);
        }

        get timestamp(): string {
            return this.resource.timestamp;
        }

        get timestampAsDate(): Date {
            return new Date(this.resource.timestamp);
        }
    };

    // TODO: this has been commented for a while- not sure what the original intentions were
    // Name the subclass
    // Object.defineProperty(DynamicStatImsObjectClass, 'name', {value: statName});

    const field_schemas = dynamicStatSchema.field_schemas;

    for (const key of Object.keys(field_schemas)) {
        Object.defineProperty(DynamicStatImsObjectClass.prototype, key, {
            get: function () {
                return this.resource[key];
            },
        });
    }
    if (dynamicStatSchema.is_relationship) {
        Object.defineProperty(DynamicStatImsObjectClass.prototype, 'end_timestamp', {
            get: function () {
                return this.resource['end_timestamp'];
            },
        });
    }

    // assetId or ipDeviceId are used around the app so need to be defined as needed as well
    const parentReference = getParentFieldKeyForParentType(dynamicStatSchema.parent_type);
    Object.defineProperty(DynamicStatImsObjectClass.prototype, parentReference, {
        get: function () {
            return this.resource[dynamicStatSchema.parent_type + '_id'];
        },
    });

    return DynamicStatImsObjectClass;
}

function createImsObjectCollectionClass(
    dynamicStatSchema: DynamicStatSchema,
    dynamicStatImsObjectClass: ConstructableClass,
    dynamicStatResource: resource.IResourceClass<unknown>,
): ConstructableDynamicStatImsObjectCollection {
    const statName = dynamicStatSchema.name;
    const statParent = dynamicStatSchema.parent_type;
    const collectionName = statName + 'Collection';
    dynamicStatSchema.is_relationship;

    const DynamicStatImsObjectCollection = class extends ImsTimeCollection {
        constructor(
            startUpRegistry: StartupRegistry,
            localStorageService: ILocalStorageService,
            $rootScope: IRootScopeService,
            $timeout: ITimeoutService,
            $interval: IIntervalService,
            clockHandler: ClockHandler,
            viewStateService: ViewStateService,
            siteStateService: SiteStateService,
            queue,
            fetcher,
            processor,
        ) {
            super(
                startUpRegistry,
                localStorageService,
                $rootScope,
                $timeout,
                $interval,
                clockHandler,
                viewStateService,
                siteStateService,
                dynamicStatResource,
                dynamicStatImsObjectClass,
                queue,
                fetcher,
                processor,
                {
                    requestLatestStats: true,
                },
            );
        }

        getLatestStatsResourceFunction() {
            return this.resourceObjectBase['latest_by_' + statParent];
        }

        extendParams(params, isLiveChunk: any = undefined) {
            const { timestamp__gte } = params;

            if (dynamicStatSchema.is_relationship) {
                // for a relationship collection, we want to pick up any stats
                //  which "fall within the range" of what we're asking for
                // this means you'll get plenty of duplicates, but duplicates are handled okay
                return {
                    // this changes our query from:
                    // - asking for stats which are in between the given range; to
                    // - asking for stats which overlap with the given range
                    //      (because we're asking for a range, and our stats represent a range, so we need range overlap)
                    ...params,
                    // so (existing param) - filter by timestamp (start_timestamp) is less than end timestamp
                    // and (new param) - filter by end_timestamp is greater than the start of our range
                    end_timestamp__gte: timestamp__gte,
                    // make sure we undo this param, we've changed the semantics of this query
                    timestamp__gte: undefined,
                };
            } else {
                return params;
            }
        }
    };

    // Name the subclass
    Object.defineProperty(DynamicStatImsObjectCollection, 'name', { value: collectionName });

    return DynamicStatImsObjectCollection;
}

function createAsRelationshipEndpoint(
    method: string,
    apiUrlRootStandard: string,
    dynamicStatName: string,
): resource.IActionDescriptor {
    return {
        method: method,
        url: `${apiUrlRootStandard}dynamic_stats/${dynamicStatName}/relationships/`,
        isArray: true,
    };
}

export const RELATIONSHIPS_ENDPOINT = 'relationships';

function withRelationshipEndpoint(
    apiUrlRoot: string,
    dynamicStatSchemaName: string,
    is_relationship: boolean,
    src: resource.IActionHash,
): resource.IActionHash {
    if (is_relationship) {
        return Object.assign(src, {
            [RELATIONSHIPS_ENDPOINT]: createAsRelationshipEndpoint(
                'POST',
                apiUrlRoot,
                dynamicStatSchemaName,
            ),
        });
    } else {
        return src;
    }
}

function createSubgroupDistinctValuesEndpoint(
    method: string,
    apiUrlRootStandard: string,
    dynamicStatName: string,
): resource.IActionDescriptor {
    return {
        method: method,
        url: `${apiUrlRootStandard}dynamic_stats/${dynamicStatName}/distinct_subgroup_values/`,
        isArray: true,
    };
}

export class DynamicStatsManager {
    _imsItemTypeRegistry: ImsItemTypeRegistry;
    _dynamicStatSchemaCollection: DynamicStatSchemaCollection;
    _resource: resource.IResourceService;
    _rootScope: IRootScopeService;
    _timeout: ITimeoutService;
    _interval: IIntervalService;
    _clockHandler: ClockHandler;
    _viewStateService: ViewStateService;
    _siteStateService: SiteStateService;
    _apiUrlRoot: string;
    _processorByDynamicStatSchemaName: Map<string, AdjustableProcessor<ImsObject>>;
    _startUpRegistry: StartupRegistry;
    _localStorageService: ILocalStorageService;

    constructor(
        startUpRegistry: StartupRegistry,
        localStorageService: ILocalStorageService,
        imsItemTypeRegistry: ImsItemTypeRegistry,
        dynamicStatSchemaCollection: DynamicStatSchemaCollection,
        $resource: resource.IResourceService,
        $rootScope: IRootScopeService,
        $timeout: ITimeoutService,
        $interval: IIntervalService,
        clockHandler: ClockHandler,
        viewStateService: ViewStateService,
        siteStateService: SiteStateService,
        apiUrlRoot: string,
    ) {
        this._startUpRegistry = startUpRegistry;
        this._localStorageService = localStorageService;
        this._imsItemTypeRegistry = imsItemTypeRegistry;
        this._dynamicStatSchemaCollection = dynamicStatSchemaCollection;
        this._resource = $resource;
        this._rootScope = $rootScope;
        this._timeout = $timeout;
        this._interval = $interval;
        this._clockHandler = clockHandler;
        this._viewStateService = viewStateService;
        this._siteStateService = siteStateService;

        if (!apiUrlRoot) {
            throw new Error(`apiUrlRoot cannot be falsey`);
        }

        this._apiUrlRoot = apiUrlRoot;
        this._processorByDynamicStatSchemaName = new Map<string, AdjustableProcessor<ImsObject>>();
    }

    createResourceInstance(dynamicStatSchema: DynamicStatSchema): resource.IResourceClass<unknown> {
        const dynamicStatName = dynamicStatSchema.name;
        const latestByEndpointName = 'latest_by_' + dynamicStatSchema.parent_type;
        const latestByEndpointPoint = {
            method: 'GET',
            url: `${this._apiUrlRoot}dynamic_stats/${dynamicStatName}/${latestByEndpointName}/`,
            isArray: true,
        };
        if (dynamicStatSchema.is_relationship)
            latestByEndpointPoint.url = `${this._apiUrlRoot}dynamic_stats/${dynamicStatName}/relationships/`;

        const distinctSubgroupValuesEndpoints: resource.IActionHash = {};
        for (const [, field_schema] of Object.entries(dynamicStatSchema.field_schemas)) {
            if (field_schema.type === 'string' && field_schema.is_subgroup) {
                distinctSubgroupValuesEndpoints[DISTINCT_SUBGROUP_VALUES_KEY] =
                    createSubgroupDistinctValuesEndpoint('GET', this._apiUrlRoot, dynamicStatName);
                break;
            }
        }

        return this._resource(
            `${this._apiUrlRoot}dynamic_stats/${dynamicStatName}/:id/`,
            { id: '@id' },
            withRelationshipEndpoint(
                this._apiUrlRoot,
                dynamicStatSchema.name,
                dynamicStatSchema.is_relationship,
                {
                    update: { method: 'PATCH' },
                    [latestByEndpointName]: latestByEndpointPoint,
                    [WITH_LOCATION_ENDPOINT_FUNC]: createWithLocationEndpoint(
                        this._apiUrlRoot,
                        `dynamic_stats/${dynamicStatSchema.name}`,
                    ),
                    ...distinctSubgroupValuesEndpoints,
                },
            ),
        );
    }

    async getDataFrequency(dynamicStatSchema: DynamicStatSchema): Promise<number | null> {
        // try to get the current view
        const currentView = this._viewStateService.getCurrentView();

        // probable that the viewStateService isn't properly ready- give up for now, we'll pick it up on the update
        if (!currentView) {
            return null; // so statHoldSeconds will be Infinity for a brief period
        }

        // nothing in View.tables
        if (!currentView.tables) {
            return dynamicStatSchema.data_frequency;
        }

        // no override for this schema
        const dynamicStatSchemaOverride = currentView.tables[dynamicStatSchema.name];
        if (!dynamicStatSchemaOverride) {
            return dynamicStatSchema.data_frequency;
        }

        // no override for data_frequency for this schema
        if (dynamicStatSchemaOverride.data_frequency === undefined) {
            return dynamicStatSchema.data_frequency;
        }

        // alright, we got an override
        return dynamicStatSchemaOverride.data_frequency;
    }

    async getStatHoldSeconds(dynamicStatSchema: DynamicStatSchema): Promise<number> {
        // get data_frequency from the dynamicStatSchema / override with View as applicable
        const dataFrequency = await this.getDataFrequency(dynamicStatSchema);

        // Interval endTime should be data_frequency * 2 to deal with a small gap in data without going white.
        // Or infinity (acting as state values)
        return getStatHoldSeconds(dataFrequency);
    }

    async createAndRegisterDynamicStat(dynamicStatSchema: DynamicStatSchema): Promise<void> {
        const statName = dynamicStatSchema.name;
        const statHoldSeconds = await this.getStatHoldSeconds(dynamicStatSchema);
        const displayName = dynamicStatSchema.display_name;
        const dynamicStatResource = this.createResourceInstance(dynamicStatSchema);
        const dynamicStatItemClass = createImsObjectClass(dynamicStatSchema, dynamicStatResource);
        const dynamicStatImsObjectCollectionClass = createImsObjectCollectionClass(
            dynamicStatSchema,
            dynamicStatItemClass,
            dynamicStatResource,
        );

        const primaryKeyProp = getParentFieldKeyForParentType(dynamicStatSchema.parent_type);

        const subgroupFieldNames = Object.keys(dynamicStatSchema.field_schemas).filter(
            (fieldName) => dynamicStatSchema.field_schemas[fieldName].is_subgroup,
        );

        if (subgroupFieldNames.length > 1) {
            throw new Error(
                `Expected no more than 1 subgroup field. Found: ${subgroupFieldNames.join(', ')}`,
            );
        }

        const subgroupField = subgroupFieldNames.length ? subgroupFieldNames[0] : null;

        let interpolator: AdjustableClosestPastSampleInterpolator;
        if (dynamicStatSchema.is_relationship) {
            interpolator = new AdjustableClosestPastSampleInterpolator(
                statHoldSeconds, // ignored by the range interpolator, just here to meet the interface elsewhere
                getDateWithinTimeRangeInterpolator,
            );
        } else {
            interpolator = new AdjustableClosestPastSampleInterpolator(statHoldSeconds);
        }

        let processor;
        if (subgroupField || dynamicStatSchema.is_relationship) {
            processor = new GroupedSampleProcessor(
                {
                    primaryKeyProp: primaryKeyProp,
                    secondaryKeyProp: subgroupField
                        ? subgroupField
                        : dynamicStatSchema.relationship_target_asset_column_name,
                    startDateProp: 'timestampAsDate',
                },
                interpolator,
            );
        } else {
            processor = new SampleProcessor(
                {
                    relationshipProp: primaryKeyProp,
                    startDateProp: 'timestampAsDate',
                },
                interpolator,
            );
        }

        const dynamicStatImsObjectCollection = new dynamicStatImsObjectCollectionClass(
            this._startUpRegistry,
            this._localStorageService,
            this._rootScope,
            this._timeout,
            this._interval,
            this._clockHandler,
            this._viewStateService,
            this._siteStateService,
            new TimeIntervalChunker(ConfigService.timeIntervalChunkDynamicStats),
            new SimpleFetcher(),
            processor,
            {},
        );

        const imsItemTypeDetails: ImsItemTypeDetails = {
            displayName: displayName ? displayName : statName,
            collection: dynamicStatImsObjectCollection,
            ItemClass: dynamicStatItemClass,
            isStat: true,
            statDefinition: dynamicStatSchema.getAsStatDefinition(),
        };

        await this._imsItemTypeRegistry.register(statName, imsItemTypeDetails);

        this._processorByDynamicStatSchemaName.set(statName, processor);
    }

    async updateProcessors(): Promise<void> {
        const dynamicStatsSchemas = await this._dynamicStatSchemaCollection.getAllAsArrayAsync();

        await Promise.all(
            dynamicStatsSchemas.map(async (dynamicStatSchema) => {
                const statHoldSeconds = await this.getStatHoldSeconds(dynamicStatSchema);
                const existingProcessor = this._processorByDynamicStatSchemaName.get(
                    dynamicStatSchema.name,
                );

                // TODO: not 100% this will happen- if it does though, potentially means we're
                // letting front-ends get out of sync with new dynamic stat schemas, so let's log
                // too see if it happens in the wild
                if (!existingProcessor) {
                    console.error(
                        `Failed to find an existing Processor for DynamicStatSchema ${dynamicStatSchema.name}`,
                    );
                    return;
                }

                existingProcessor.setStatHoldSeconds(statHoldSeconds);
            }),
        );
    }

    /**
     * Registers dynamic stats in the ImsItemTypeRegistry.
     * IMPORTANT! We must wait for dynamic stats schemas to be loaded
     * before we fire off loadInitialViewAndController. Some views (eg movement in Imdemo)
     * will fail (the opposite of lightweight and blazing fast) if we attempt to load views before dynamic stats
     */
    async registerDynamicStats(): Promise<void> {
        const dynamicStatsSchemas = await this._dynamicStatSchemaCollection.getAllAsArrayAsync();

        await Promise.all(
            dynamicStatsSchemas.map((dynamicStatSchema) => {
                return this.createAndRegisterDynamicStat(dynamicStatSchema);
            }),
        );

        // This is called here so that the imsItemTypeRegistry will only notify listeners when dynamic stat schemas
        // have all loaded and been registered
        await this._imsItemTypeRegistry.notifyListenersWhenReady();
        await this._viewStateService.loadInitialViewAndController();

        // call this again any time the view changes- cater for any overrides from a changed view or any
        // new dynamic stat schmeas in general
        this._viewStateService.onViewSelectionChanged(async () => {
            await this.updateProcessors();
        });
    }
}
